import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/button";
import { useGetPaymentFeeMutation } from "../../../../../service/charge-service";
import { BulkPaymentMethod } from "../../bulk-purchase.types";
import { formatCurrency } from "../../../../../utils";
import styles from "./bulk-confirmation.module.scss";

interface BulkConfirmationProps {
	total: number;
	prepaidCount: number;
	postpaidCount: number;
	itemCount: number;
	currency: string;
	hasWallet: boolean;
	walletBalance: number;
	onPay: (method: BulkPaymentMethod, fee: number) => void;
}

export const BulkConfirmation: React.FC<BulkConfirmationProps> = ({
	total,
	prepaidCount,
	postpaidCount,
	itemCount,
	currency,
	hasWallet,
	walletBalance,
	onPay,
}) => {
	const { t } = useTranslation();
	const [getPaymentFee, { isLoading: isCalculatingFee }] = useGetPaymentFeeMutation();
	const [fee, setFee] = useState(0);
	const [method, setMethod] = useState<BulkPaymentMethod>(hasWallet ? "wallet" : "card");

	useEffect(() => {
		let active = true;
		getPaymentFee({ amount: total, currency })
			.unwrap()
			.then((res: any) => {
				if (active) setFee(res?.estimationFees?.[0]?.paymentChannels?.[0]?.totalFee?.amount ?? 0);
			})
			.catch(() => {
				if (active) setFee(0);
			});
		return () => {
			active = false;
		};
	}, [total, currency, getPaymentFee]);

	const grandTotal = total + fee;
	const insufficient = method === "wallet" && walletBalance < grandTotal;

	const summaryRows = useMemo(
		() => [
			{ label: t("buyElectricity.bulk.purchaseType"), value: t("buyElectricity.bulk.bulk") },
			{ label: t("buyElectricity.bulk.totalMeters"), value: String(itemCount) },
			{ label: t("buyElectricity.bulk.prepaidCount"), value: String(prepaidCount) },
			{ label: t("buyElectricity.bulk.postpaidCount"), value: String(postpaidCount) },
			{ label: t("buyElectricity.totalAmount"), value: formatCurrency(total, { currency }) },
			{ label: t("buyElectricity.bulk.fee"), value: isCalculatingFee ? "…" : formatCurrency(fee, { currency }) },
		],
		[t, itemCount, prepaidCount, postpaidCount, total, fee, currency, isCalculatingFee]
	);

	return (
		<div className={styles.container}>
			<div className={styles.card}>
				{summaryRows.map(row => (
					<div key={row.label} className={styles.summaryRow}>
						<span className={styles.summaryLabel}>{row.label}</span>
						<span className={styles.summaryValue}>{row.value}</span>
					</div>
				))}
			</div>

			<div className={styles.card}>
				<p className={styles.sectionLabel}>{t("buyElectricity.bulk.choosePaymentMethod")}</p>

				{hasWallet && (
					<button
						type="button"
						className={`${styles.method} ${method === "wallet" ? styles.methodActive : ""}`}
						onClick={() => setMethod("wallet")}
					>
						<span className={styles.methodMain}>
							<span className={styles.methodTitle}>{t("buyElectricity.bulk.payWithWallet")}</span>
							<span className={styles.methodSub}>
								{t("buyElectricity.web.balanceLabel") || "Balance:"} {formatCurrency(walletBalance, { currency })}
							</span>
						</span>
						<span className={styles.radioOuter}>{method === "wallet" && <span className={styles.radioInner} />}</span>
					</button>
				)}

				<button
					type="button"
					className={`${styles.method} ${method === "card" ? styles.methodActive : ""}`}
					onClick={() => setMethod("card")}
				>
					<span className={styles.methodMain}>
						<span className={styles.methodTitle}>{t("buyElectricity.web.debitCreditCard")}</span>
						<span className={styles.methodSub}>{t("buyElectricity.bulk.paymentHubHint")}</span>
					</span>
					<span className={styles.radioOuter}>{method === "card" && <span className={styles.radioInner} />}</span>
				</button>

				{insufficient && (
					<div className={styles.insufficient}>
						<strong>{t("buyElectricity.bulk.insufficientBalance")}</strong>
						<span>{t("buyElectricity.bulk.topUpOrUseAnother")}</span>
					</div>
				)}
			</div>

			<div className={styles.footer}>
				<Button variant="primary" fullWidth className={styles.primaryAction} disabled={insufficient} onClick={() => onPay(method, fee)}>
					{t("buyElectricity.bulk.payAmount", { amount: formatCurrency(grandTotal, { currency }) })}
				</Button>
			</div>
		</div>
	);
};

export default BulkConfirmation;
